import { useState, useEffect } from 'react';
import {
  Outlet,
  Link as RouterLink,
  useLocation,
  useNavigate,
} from 'react-router';
import { List, ListItemButton, ListItemText, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import AppSidebarLayout from '../shared/AppSidebarLayout';

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import toTitleCase from '../../util/toTitleCase';

interface DocsNavStructure {
  [key: string]: string | DocsNavStructure;
}

type DocsNavValue = string | DocsNavStructure;

interface NavItemProps {
  name: string;
  value: DocsNavValue;
  level?: number;
}

const NavItem = ({ name, value, level = 1 }: NavItemProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);

  const friendly_name = toTitleCase(name);

  const handleToggle = (): void => {
    setOpen(!open);
  };

  // If value is a string, it's a markdown file; render a link
  if (typeof value === 'string') {
    return (
      <ListItemButton
        component={RouterLink}
        to={`/docs/${value}`}
        sx={{ pl: 4 * level }}
      >
        <ListItemText primary={friendly_name} />
      </ListItemButton>
    );
  }

  // If value is an object, it's a directory; render a collapsible list
  return (
    <>
      <ListItemButton onClick={handleToggle} sx={{ pl: 4 * level }}>
        <ListItemText primary={friendly_name} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout='auto' unmountOnExit>
        <List component='div' disablePadding>
          {Object.entries(value).map(([key, val]) => (
            <NavItem key={key} name={key} value={val} level={level + 1} />
          ))}
        </List>
      </Collapse>
    </>
  );
};

const DocsNavigation = (): React.JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pages, setPages] = useState<Record<string, DocsNavValue>>({});

  const app = location.pathname
    .split('/')
    .filter((val) => Boolean(val))
    .at(-1);

  useEffect(() => {
    if (app === 'docs') {
      navigate('/docs/introduction');
    }
  }, [app, navigate]);

  useEffect(() => {
    fetch('/docs.json')
      .then((response) => response.json())
      .then((data) => setPages(data))
      .catch((error) => console.error('Error loading docs list:', error));
  }, []);

  const NavMenuContent = (
    <List component='nav'>
      {Object.entries(pages).map(([key, value]) => (
        <NavItem key={key} name={key} value={value} />
      ))}
    </List>
  );

  return (
    <AppSidebarLayout
      headerTitle={'Documentation'}
      HeaderIcon={ArticleOutlinedIcon}
      SidebarContent={NavMenuContent}
    >
      <Outlet />
    </AppSidebarLayout>
  );
};
export default DocsNavigation;
